'use client'

import { Loader2, MoveRightIcon } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import React, { useEffect, useState, type FormEvent, type HTMLAttributes } from 'react'

import {
  lowestLuggagePriceCents,
  usePricingRatesApi,
  type PricingShipPointInput,
} from '@/api/usePricingRatesApi'
import { useAutoCompleteAddress, type UseAutoCompleteAddress } from '@/hooks/useAutoCompleteAddress'
import { useBookingWidgetTracking } from '@/hooks/useBookingWidgetTracking'
import { buildBookingWidgetQuery, redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'

import { AddressAutoCompleteInput } from './AddressAutoCompleteInput'
import { SuitcaseIcon } from './icons/Suitcase'
import { Button } from './ui/Button'
import { cn } from './ui/cn'

interface ShippingWidgetProps extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  className?: string
}

// Fallback shown before both endpoints are picked, or if pricing can't be
// fetched. Matches the previous static copy.
const DEFAULT_STARTING_PRICE = 54

// Build a GraphQL ShipPointInput from an autocomplete field for a *pricing*
// estimate.
//
// Always price off the resolved address, never the recognizedPlace id. The
// getPricingRates resolver wraps its input in ShipPoint.new(...) and
// Services::Quotes::Pricing::Rates#resolve_address! returns any ShipPoint
// as-is — it only classifies raw {id, type} hashes into a facility/club/place
// address. A bare {id} therefore arrives with no zip/state and can't be rate-
// zoned, so facilities/clubs (which set recognizedPlace) would silently fail
// and fall back to the default price. useAutoCompleteAddress populates the
// club's ship-to address alongside recognizedPlace, so the address branch
// covers Google places and recognized places alike. (The submit hand-off still
// prefers recognizedPlace.id via buildBookingWidgetQuery to lock the facility.)
const toShipPoint = (autoComplete: UseAutoCompleteAddress): PricingShipPointInput | undefined => {
  const address = autoComplete.address
  if (address && (address.zip || address.city)) {
    return {
      address1: address.address_1 || undefined,
      city: address.city || undefined,
      state: address.state || undefined,
      postalCode: address.zip || undefined,
      countryCode: address.country_code || undefined,
      addressType: address.address_type || undefined,
    }
  }

  return undefined
}

/**
 * Ship&Play home-hero booking widget.
 *
 * Ports the visual design from playground-shipplay's ShippingWidget and wires
 * origin/destination inputs to Kettle's real place autocomplete
 * (useAutoCompleteAddress → useAutoCompleteApi + Google Places details).
 *
 * Submit hand-off contract:
 *   - buildBookingWidgetQuery prefers each side's recognizedPlace.id
 *     (shipping_from / shipping_to), falls back to the raw AutoCompleteAddress
 *     (o_address / o_city / … and d_address / d_city / …). Marketing +
 *     attribution params are preserved via appendMarketingParams.
 *   - redirectFromBookingWidget defaults to /book/ship; NEXT_PUBLIC_BOOKING_WIDGET_TARGET
 *     (or a localStorage['booking-widget-target'] override) can flip it to /tac-book.
 */
export const ShippingWidget = ({
  className,
  ...props
}: ShippingWidgetProps): React.ReactElement => {
  const searchParams = useSearchParams()
  const originAutoComplete = useAutoCompleteAddress()
  const destinationAutoComplete = useAutoCompleteAddress()
  const { trackOriginEntered, trackDestinationEntered, trackCheckoutStarted } =
    useBookingWidgetTracking()
  const [submitting, setSubmitting] = useState(false)

  // Dynamic "Starting at" price: once both endpoints resolve, fetch route
  // pricing via GraphQL (getPricingRates) and show the lowest luggage rate.
  const originShipPoint = toShipPoint(originAutoComplete)
  const destinationShipPoint = toShipPoint(destinationAutoComplete)
  const { data: pricingRates, isFetching: isFetchingRates } = usePricingRatesApi(
    originShipPoint && destinationShipPoint
      ? { origin: originShipPoint, destination: destinationShipPoint }
      : undefined,
  )
  const startingPriceCents = lowestLuggagePriceCents(pricingRates)
  const quotedPrice = startingPriceCents != null ? Math.floor(startingPriceCents / 100) : null

  // Remember the last real quote. Changing an endpoint swaps the query key, so
  // React Query's `data` briefly goes undefined before `isFetching` flips true —
  // without this the price would flash back to the $54 default between quotes.
  // Hold the previous price until the next one resolves; only ever show $54
  // before the first successful quote.
  const [lastQuotedPrice, setLastQuotedPrice] = useState<number | null>(null)
  useEffect(() => {
    if (quotedPrice != null) setLastQuotedPrice(quotedPrice)
  }, [quotedPrice])

  const startingPrice = quotedPrice ?? lastQuotedPrice ?? DEFAULT_STARTING_PRICE

  // The submit hand-off is a hard navigation; if the browser restores this page
  // from bfcache (user hits Back from /book/ship or the TAC domain), React
  // state is frozen at `submitting: true` and the CTA is stuck on
  // "One moment…". `pageshow` with `persisted: true` fires only on bfcache
  // restore — reset there so the button reverts to "Let's Go".
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) setSubmitting(false)
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)

    // No trip type selector on this widget — the /book/ship funnel seeds a
    // one-way leg unless the user later opts into roundtrip.
    trackCheckoutStarted({
      tripType: 'oneway',
      origin: originAutoComplete.address?.formatted_address,
      destination: destinationAutoComplete.address?.formatted_address,
    })

    const queryString = buildBookingWidgetQuery({
      originAddress: originAutoComplete.address,
      destinationAddress: destinationAutoComplete.address,
      originRecognizedPlace: originAutoComplete.recognizedPlace,
      destinationRecognizedPlace: destinationAutoComplete.recognizedPlace,
      searchParams,
    })
    redirectFromBookingWidget(queryString)
  }

  return (
    <form
      {...props}
      onSubmit={handleSubmit}
      className={cn(
        // `relative z-30` lifts the widget's stacking context above later page
        // sections. The .snplay-glass backdrop-filter already makes this form a
        // stacking context, trapping the suggestion dropdown's z-50 inside it;
        // without a positive z-index here the form ties at z-auto with the logo
        // AutoScroller (its own transform-animation stacking context) and loses
        // on DOM order, so the scrolling logos paint over the open dropdown.
        'snplay-glass snplay:relative snplay:z-30 snplay:rounded-2xl snplay:p-3.75',
        'snplay:transition snplay:has-[:focus-within]:bg-background/60',
        'snplay:shadow-[0_1px_3px_rgba(0,0,0,0.1),0_8px_24px_rgba(0,0,0,0.1)]',
        'snplay:gap-y-4 snplay:xs:flex snplay:xs:flex-wrap',
        'snplay:xs:items-center snplay:xs:gap-x-6',
        'snplay:lg:flex-nowrap snplay:lg:pl-4.75',
        className,
      )}
    >
      <AddressAutoCompleteInput
        label="Ship from"
        placeholder="Where from?"
        autoComplete={originAutoComplete}
        onSelect={(item) => trackOriginEntered(item.formatted_address)}
        className={cn(
          'snplay:max-xs:mb-2 snplay:max-xs:rounded-lg',
          'snplay:max-xs:border snplay:max-xs:border-background/60',
          'snplay:max-xs:bg-background/60 snplay:max-xs:p-3',
          'snplay:flex-[2] snplay:sm:flex-[calc(50%-0.75rem)] snplay:md:flex-[calc(38%-1rem)]',
        )}
      />
      <AddressAutoCompleteInput
        label="Ship to"
        placeholder="Where to?"
        autoComplete={destinationAutoComplete}
        onSelect={(item) => trackDestinationEntered(item.formatted_address)}
        className={cn(
          'snplay:max-xs:mb-2 snplay:max-xs:rounded-lg',
          'snplay:max-xs:border snplay:max-xs:border-background/60',
          'snplay:max-xs:bg-background/60 snplay:max-xs:p-3',
          'snplay:flex-[2] snplay:sm:flex-[calc(50%-0.75rem)] snplay:md:flex-[calc(38%-1rem)]',
        )}
      />
      <div
        className={cn(
          'snplay:max-md:flex snplay:max-md:w-full snplay:max-xs:mb-2 snplay:max-xs:mt-4',
          'snplay:sm:min-w-32 snplay:md:flex-[calc(24%-1rem)] snplay:lg:flex-1',
        )}
      >
        <span className="snplay:mb-1 snplay:mr-1 snplay:block snplay:text-sm snplay:font-medium">
          Starting at
        </span>
        <span
          className="snplay:flex snplay:h-7 snplay:items-center snplay:text-lg snplay:font-semibold"
          role="status"
          aria-live="polite"
        >
          {isFetchingRates ? (
            <Loader2
              className="snplay:size-5 snplay:animate-spin snplay:text-primaryDark"
              aria-label="Loading rate"
            />
          ) : (
            <span>
              ${startingPrice} <span className="snplay:text-sm snplay:font-normal">each way</span>
            </span>
          )}
        </span>
      </div>
      <Button
        type="submit"
        variant="primary"
        size="xl"
        className="snplay:max-lg:w-full"
        disabled={submitting}
      >
        <SuitcaseIcon className="snplay:size-6 snplay:text-primary" />
        {submitting ? 'One moment…' : "Let's Go"}
        <MoveRightIcon className="snplay:size-6" />
      </Button>
    </form>
  )
}
